/**
 * Light / dark preference.
 *
 * Three states, not two. "System" is the default and the honest one - most
 * people have already told their OS what they want. The explicit choices write
 * `data-theme` on `<html>`, which is what the `:root[data-theme="dark"]` and
 * `:root:not([data-theme="light"])` blocks in theme.css key off.
 */
import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'botguide.theme';

function readStored(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    // Private browsing, or storage disabled. The system default is fine.
  }
  return 'system';
}

function apply(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
}

export function useThemePreference(): {
  preference: ThemePreference;
  setPreference: (next: ThemePreference) => void;
} {
  const [preference, setState] = useState<ThemePreference>(readStored);

  useEffect(() => {
    apply(preference);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisting is a smaller failure than not switching.
    }
  }, []);

  return { preference, setPreference };
}
